/**
 * Bookmark Enrichment Module
 *
 * Centralized logic for enriching bookmarks with:
 * - Metadata extraction (title, description, content, etc.)
 * - Rule-based auto-tagging
 * - AI-powered tagging (when configured)
 * - AI-powered summarization (when configured)
 *
 * This module is used across:
 * - Bookmark creation (TopBar)
 * - Bookmark refresh (BookmarkCard)
 * - Bookmark editing (BookmarkCard)
 */

import { metadataApi } from './api';
import { extractDomain, getFaviconUrl } from './metadata';
import { generateAutoTags } from './tagging';
import { isAIConfigured, generateAITags, generateAISummary } from './ai';

/**
 * Metadata extracted from URL
 */
export interface ExtractedMetadata {
  title: string;
  description: string;
  content?: string | null;
  excerpt?: string | null;
  author?: string | null;
  site_name?: string | null;
  published_at?: string | null;
  reading_time?: number | null;
  thumbnail?: string | null;
  domain: string;
  favicon_url: string;
}

/**
 * Options for bookmark enrichment
 */
export interface EnrichmentOptions {
  /** URL to enrich */
  url: string;
  /** Existing title (optional, for comparison) */
  existingTitle?: string;
  /** Existing description (optional, for comparison) */
  existingDescription?: string;
  /** Existing tags (to avoid duplicates) */
  existingTags?: string[];
  /** Whether to include AI features */
  includeAI?: boolean;
  /** Specific AI features to include */
  aiFeatures?: {
    tags?: boolean;
    summary?: boolean;
  };
  /** Progress callback for UI updates */
  onProgress?: (step: EnrichmentStep) => void;
}

/**
 * Enrichment progress steps
 */
export type EnrichmentStep =
  | 'fetching_metadata'
  | 'generating_auto_tags'
  | 'generating_ai_tags'
  | 'generating_ai_summary'
  | 'complete';

/**
 * Result of bookmark enrichment
 */
export interface EnrichmentResult {
  /** Extracted metadata from URL */
  metadata: ExtractedMetadata;
  /** Rule-based auto-generated tags */
  autoTags: string[];
  /** AI-generated tags (if AI enabled) */
  aiTags?: string[];
  /** AI-generated summary (if AI enabled) */
  aiSummary?: string;
  /** Combined tags (auto + AI, deduplicated) */
  allTags: string[];
  /** New tags that weren't in existingTags */
  newTags: string[];
}

/**
 * Enrich a bookmark with metadata and tags
 *
 * @param options Enrichment options
 * @returns Enrichment result with metadata, tags, and optional AI content
 */
export async function enrichBookmark(
  options: EnrichmentOptions
): Promise<EnrichmentResult> {
  const {
    url,
    existingTags = [],
    includeAI = false,
    aiFeatures = { tags: true, summary: true },
    onProgress,
  } = options;

  // Step 1: Fetch metadata
  onProgress?.('fetching_metadata');
  let metadata: ExtractedMetadata;

  try {
    const fetchedMetadata = await metadataApi.extract(url);
    metadata = {
      title: fetchedMetadata.title || url,
      description: fetchedMetadata.description || '',
      content: fetchedMetadata.content || null,
      excerpt: fetchedMetadata.excerpt || null,
      author: fetchedMetadata.author || null,
      site_name: fetchedMetadata.site_name || null,
      published_at: fetchedMetadata.published_at || null,
      reading_time: fetchedMetadata.reading_time || null,
      thumbnail: fetchedMetadata.thumbnail || null,
      domain: extractDomain(url),
      favicon_url: getFaviconUrl(url),
    };
  } catch (error) {
    console.error('Metadata extraction failed:', error);
    // Fallback to basic metadata
    metadata = {
      title: url,
      description: '',
      domain: extractDomain(url),
      favicon_url: getFaviconUrl(url),
    };
  }

  // Step 2: Generate auto tags (rule-based)
  onProgress?.('generating_auto_tags');
  const autoTags = generateAutoTags(url, metadata.title, metadata.description);

  // Collect all tags
  let aiTags: string[] | undefined;
  let aiSummary: string | undefined;

  // Step 3: AI tagging (if enabled)
  if (includeAI && aiFeatures.tags && isAIConfigured()) {
    onProgress?.('generating_ai_tags');
    try {
      const existingLower = new Set([
        ...existingTags.map((t) => t.toLowerCase()),
        ...autoTags.map((t) => t.toLowerCase()),
      ]);
      const result = await generateAITags(
        url,
        metadata.title,
        metadata.description,
        [...existingTags, ...autoTags]
      );
      // Filter out tags that already exist (case-insensitive)
      aiTags = result.tags.filter(
        (t) => !existingLower.has(t.toLowerCase())
      );
    } catch (error) {
      console.error('AI tagging failed:', error);
      // Continue without AI tags
    }
  }

  // Step 4: AI summary (if enabled)
  if (includeAI && aiFeatures.summary && isAIConfigured()) {
    onProgress?.('generating_ai_summary');
    try {
      const result = await generateAISummary(
        url,
        metadata.title,
        metadata.description,
        metadata.content || undefined
      );
      aiSummary = result.summary;
    } catch (error) {
      console.error('AI summary failed:', error);
      // Continue without AI summary
    }
  }

  // Combine all tags (deduplicated)
  const allTagsSet = new Set<string>();
  const existingLower = new Set(existingTags.map((t) => t.toLowerCase()));

  // Add auto tags
  for (const tag of autoTags) {
    allTagsSet.add(tag);
  }

  // Add AI tags
  if (aiTags) {
    for (const tag of aiTags) {
      allTagsSet.add(tag);
    }
  }

  const allTags = Array.from(allTagsSet);

  // Find new tags (not in existing)
  const newTags = allTags.filter(
    (t) => !existingLower.has(t.toLowerCase())
  );

  onProgress?.('complete');

  return {
    metadata,
    autoTags,
    aiTags,
    aiSummary,
    allTags,
    newTags,
  };
}

/**
 * Quick enrichment - metadata and auto tags only (no AI)
 */
export async function quickEnrich(url: string): Promise<EnrichmentResult> {
  return enrichBookmark({
    url,
    includeAI: false,
  });
}

/**
 * Full enrichment - metadata, auto tags, and AI features
 */
export async function fullEnrich(
  url: string,
  existingTags?: string[],
  onProgress?: (step: EnrichmentStep) => void
): Promise<EnrichmentResult> {
  return enrichBookmark({
    url,
    existingTags,
    includeAI: true,
    aiFeatures: { tags: true, summary: true },
    onProgress,
  });
}

/**
 * Check if AI enrichment is available
 */
export function isAIEnrichmentAvailable(): boolean {
  return isAIConfigured();
}
